import React, { useEffect, useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Chip,
  TextField,
  MenuItem,
  Stack,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { format } from 'date-fns';
import DataTable from 'src/components/DataTable';
import { Column, PagingType, Row } from 'src/components/DataTable/types';
import { SolutionOmniverseSession } from '../../types';

interface SolutionOmniverseSessionsModalProps {
  open: boolean;
  onClose: () => void;
  sessions: SolutionOmniverseSession[] | undefined;
  loading: boolean;
  totalItems?: number;
  onPageChange: (page: number, pageSize: number) => void;
  onFilterChange: (filters: {
    status?: number;
  }) => void;
  currentPage: number;
  currentPageSize: number;
  twinId?: string;
}

enum SessionStatus {
  Connecting = 0,
  Active = 1,
  Idle = 2,
  Alive = 3,
  Stopped = 4,
}

const statusLabels: Record<SessionStatus, string> = {
  [SessionStatus.Connecting]: 'Connecting',
  [SessionStatus.Active]: 'Active',
  [SessionStatus.Idle]: 'Idle',
  [SessionStatus.Alive]: 'Alive',
  [SessionStatus.Stopped]: 'Stopped',
};

// Flattened session row type for DataTable
interface SessionRow extends Row {
  id: string;
  appTitle: string;
  appDescription: string;
  userName: string;
  status: string;
  version: string;
  category: string;
  startDate: string;
  endDate: string;
  createdBy: string;
}

const SolutionOmniverseSessionsModal: React.FC<
  SolutionOmniverseSessionsModalProps
> = ({
  open,
  onClose,
  sessions,
  loading,
  totalItems,
  onPageChange,
  onFilterChange,
  currentPage,
  currentPageSize,
  twinId,
}) => {
  const [filters, setFilters] = useState<{
    status: string;
  }>({
    status: '',
  });

  useEffect(() => {
    if (open) {
      // Reset filters when modal opens
      setFilters({
        status: '',
      });
    }
  }, [open]);

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MM/dd/yyyy HH:mm');
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('active') || statusLower.includes('alive')) {
      return 'success';
    }
    if (statusLower.includes('stopped')) {
      return 'default';
    }
    if (statusLower.includes('connecting') || statusLower.includes('idle')) {
      return 'warning';
    }
    return 'default';
  };

  const handleFilterChange = (
    field: 'status',
    value: string
  ) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);

    // Apply filters
    const filterParams: {
      status?: number;
    } = {};

    if (newFilters.status) {
      filterParams.status = parseInt(newFilters.status, 10);
    }

    onFilterChange(filterParams);
  };

  const columns: Column<SessionRow>[] = useMemo(
    () => [
      {
        id: 'id',
        title: 'Session ID',
        Component: ({ value }) => (
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              maxWidth: 100,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={value as string}
          >
            {(value as string).substring(0, 8)}...
          </Typography>
        ),
      },
      {
        id: 'appTitle',
        title: 'Application',
        Component: ({ item }) => (
          <Box>
            <Typography variant="body2">
              {item?.appTitle || 'N/A'}
            </Typography>
            {item?.appDescription && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  maxWidth: 200,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={item.appDescription}
              >
                {item.appDescription}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        id: 'userName',
        title: 'User',
        Component: ({ value }) => (
          <Typography variant="body2">
            {(value as string) || 'N/A'}
          </Typography>
        ),
      },
      {
        id: 'status',
        title: 'Status',
        Component: ({ value }) => (
          <Chip
            label={value as string}
            color={getStatusColor(value as string)}
            size="small"
            sx={{ textTransform: 'capitalize' }}
          />
        ),
      },
      {
        id: 'version',
        title: 'Version',
        Component: ({ value }) => (
          <Typography variant="body2">
            {(value as string) || 'N/A'}
          </Typography>
        ),
      },
      {
        id: 'category',
        title: 'Category',
        Component: ({ value }) => (
          <Typography variant="body2">
            {(value as string) || 'N/A'}
          </Typography>
        ),
      },
      {
        id: 'startDate',
        title: 'Start Date',
        Component: ({ value }) => (
          <Typography variant="body2">{formatDate(value as string)}</Typography>
        ),
      },
      {
        id: 'endDate',
        title: 'End Date',
        Component: ({ value }) => (
          <Typography variant="body2">
            {value ? formatDate(value as string) : '-'}
          </Typography>
        ),
      },
      {
        id: 'createdBy',
        title: 'Created By',
        Component: ({ value }) => (
          <Typography variant="body2">{(value as string) || 'N/A'}</Typography>
        ),
      },
    ],
    []
  );

  // Transform sessions data to flat row structure
  const tableData: SessionRow[] = useMemo(() => {
    if (!sessions) return [];

    return sessions.map((session) => ({
      id: session.id,
      appTitle: session.app?.title || '',
      appDescription: session.app?.description || '',
      userName: session.userName || session.userId || '',
      status: session.status,
      version: session.app?.version || '',
      category: session.app?.category || '',
      startDate: session.startDate,
      endDate: session.endDate,
      createdBy: session.createdBy,
    }));
  }, [sessions]);

  const handlePageIndexChange = (newPage: number) => {
    onPageChange(newPage + 1, currentPageSize); // API expects 1-based page index
  };

  const handlePageSizeChange = (newPageSize: number) => {
    onPageChange(1, newPageSize); // Reset to first page when changing page size
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '80vh',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0',
          pb: 2,
        }}
      >
        <Typography variant="h6" component="div">
          Omniverse Sessions
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        {/* Twin ID Info and Status Filter */}
        <Stack direction="row" spacing={2} sx={{ mb: 3, alignItems: 'center' }}>
          {twinId && (
            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1, flex: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Twin ID:
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {twinId}
              </Typography>
            </Box>
          )}

          <TextField
            label="Status"
            variant="outlined"
            size="small"
            select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value={SessionStatus.Connecting}>Connecting</MenuItem>
            <MenuItem value={SessionStatus.Active}>Active</MenuItem>
            <MenuItem value={SessionStatus.Idle}>Idle</MenuItem>
            <MenuItem value={SessionStatus.Alive}>Alive</MenuItem>
            <MenuItem value={SessionStatus.Stopped}>Stopped</MenuItem>
          </TextField>
        </Stack>

        {/* Data Table */}
        <DataTable<SessionRow>
          data={tableData}
          columns={columns}
          loading={loading}
          paging={PagingType.Remote}
          pageIndex={currentPage - 1} // DataTable expects 0-based index
          pageSize={currentPageSize}
          totalItems={totalItems || tableData.length}
          onPageIndexChange={handlePageIndexChange}
          onPageSizeChange={handlePageSizeChange}
          sx={{
            '& .MuiTableCell-root': {
              py: 1.5,
            },
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default SolutionOmniverseSessionsModal;
